// src/App.js
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from './firebase/config';
import { setUser, logout } from './redux/slices/authSlice';
import { loadUserMonitors } from './redux/slices/monitorSlice';
import { loadUserSignals } from './redux/slices/signalSlice';
import { toggleTheme, openAuthModal, setActiveSection } from './redux/slices/uiSlice';
import './App.css';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import Dashboard from './components/Dashboard';
import TradingBot from './components/TradingBot';
import Monitors from './components/Monitors';
import SignalHistory from './components/SignalHistory';
import Settings from './components/Settings';
import AuthModal from './components/AuthModal';
import TelegramReminderModal from './components/TelegramReminderModal';
import AnimatedLoader from './components/AnimatedLoader';
import { Toaster } from 'react-hot-toast';
import { io } from 'socket.io-client';
import { FaMoon, FaSun } from 'react-icons/fa';
import axios from 'axios';

const API_BASE_URL = 'https://gritwears.store/api';

let socket = null;

function App() {
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { theme, sidebarCollapsed, activeSection } = useSelector((state) => state.ui);
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState({ activeCount: 0, signalCount: 0, uptime: 0, lastSignal: '-' });
  const socketConnectedRef = useRef(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showTelegramReminder, setShowTelegramReminder] = useState(false);
  const [telegramConfigured, setTelegramConfigured] = useState(false);
  const [hasCheckedTelegram, setHasCheckedTelegram] = useState(false);

  useEffect(() => {
    if (theme === 'dark') {
      document.body.classList.add('dark-mode');
      document.body.classList.remove('light-mode');
    } else {
      document.body.classList.add('light-mode');
      document.body.classList.remove('dark-mode');
    }
  }, [theme]);

  useEffect(() => {
    const interval = setInterval(() => {
      setStats(prev => ({ ...prev, uptime: prev.uptime + 1 }));
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  const checkTelegramConfig = async (userId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/telegram/${userId}`);
      const config = response.data;
      const hasConfig = !!(config.botToken && config.chatId);
      setTelegramConfigured(hasConfig);
      const reminderDismissed = localStorage.getItem(`telegram_reminder_dismissed_${userId}`);
      if (!hasConfig && !reminderDismissed) {
        setShowTelegramReminder(true);
      }
    } catch (error) {
      console.error('Error checking Telegram config:', error);
    } finally {
      setHasCheckedTelegram(true);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        console.log('User authenticated:', firebaseUser.uid);
        dispatch(setUser({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
          displayName: firebaseUser.displayName || firebaseUser.email.split('@')[0],
          photoURL: firebaseUser.photoURL,
        }));
        
        await checkTelegramConfig(firebaseUser.uid);
        
        if (!socketConnectedRef.current) {
          if (socket) socket.disconnect();
          socket = io('http://localhost:3001', { query: { userId: firebaseUser.uid }, transports: ['websocket', 'polling'] });
          socket.on('connect', () => { console.log('✅ Socket connected for user:', firebaseUser.uid); socketConnectedRef.current = true; });
          socket.on('disconnect', () => { console.log('❌ Socket disconnected'); socketConnectedRef.current = false; });
          socket.on('signal', (signal) => {
            console.log('📢 New signal received:', signal);
            setSignals(prev => [signal, ...prev].slice(0, 100));
            setStats(prev => ({ ...prev, signalCount: prev.signalCount + 1, lastSignal: new Date(signal.timestamp).toLocaleTimeString() }));
          });
          socket.on('monitor_stopped', (data) => { console.log('Monitor stopped:', data); dispatch(loadUserMonitors(firebaseUser.uid)); });
          socket.on('error', (error) => { console.error('Socket error:', error); });
          socket.on('account_update', (account) => { console.log('📊 Account update received:', account); });
          socket.on('position_opened', (position) => { console.log('📈 Position opened:', position); });
          socket.on('position_closed', (position) => { console.log('📉 Position closed:', position); });
          socket.on('trading_error', (error) => { console.error('Trading error:', error); });
        }
        
        await dispatch(loadUserMonitors(firebaseUser.uid));
        await dispatch(loadUserSignals(firebaseUser.uid));
      } else {
        console.log('User not authenticated');
        dispatch(logout());
        setTelegramConfigured(false);
        setShowTelegramReminder(false);
        if (socket) {
          socket.disconnect();
          socket = null;
          socketConnectedRef.current = false;
        }
      }
      setIsInitialLoad(false);
    });

    return () => {
      unsubscribe();
      if (socket) {
        socket.disconnect();
        socket = null;
      }
    };
  }, [dispatch]);

  const handleConfigureTelegram = () => {
    dispatch(setActiveSection('settings'));
    setShowTelegramReminder(false);
  };

  const handleDismissReminder = () => {
    if (user?.uid) {
      localStorage.setItem(`telegram_reminder_dismissed_${user.uid}`, 'true');
    }
    setShowTelegramReminder(false);
  };

  if (isInitialLoad) {
    return <AnimatedLoader />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <Toaster position="top-right" />
        <AuthModal isOpenByDefault={true} />
      </>
    );
  }

  return (
    <div className="app">
      <Toaster position="top-right" />
      <AuthModal />
      <TelegramReminderModal isOpen={showTelegramReminder} onClose={handleDismissReminder} onConfigure={handleConfigureTelegram} />
      <Sidebar />
      <Navbar />
      <main className={`main-content ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="header">
          <div className="header-title">
            <h1>
              {activeSection === 'dashboard' && 'Trading Dashboard'}
              {activeSection === 'trading' && 'Trading Bot'}
              {activeSection === 'monitors' && 'Active Monitors'}
              {activeSection === 'signals' && 'Signal History'}
              {activeSection === 'settings' && 'Settings'}
            </h1>
            <p>
              {activeSection === 'dashboard' && 'Real-time CHoCH, MSS & CISD Detection'}
              {activeSection === 'trading' && 'Automated Trading with Real-time Position Tracking'}
              {activeSection === 'monitors' && 'Manage your active trading pairs'}
              {activeSection === 'signals' && 'View all trading signals and patterns'}
              {activeSection === 'settings' && 'Configure your bot settings'}
            </p>
          </div>
          <div className="header-actions">
            <div className="theme-toggle desktop-only" onClick={() => dispatch(toggleTheme())}>
              {theme === 'light' ? <FaMoon /> : <FaSun />}
            </div>
            <div className="user-menu desktop-only">
              <div className="user-avatar">
                {user?.photoURL ? <img src={user.photoURL} alt="avatar" className="avatar-image" /> : (user?.displayName?.charAt(0).toUpperCase() || user?.email?.charAt(0).toUpperCase() || 'U')}
              </div>
            </div>
          </div>
        </div>

        {activeSection === 'dashboard' && <Dashboard stats={stats} socket={socket} onMonitorStart={() => dispatch(loadUserMonitors(user?.uid))} />}
        {activeSection === 'trading' && <TradingBot />}
        {activeSection === 'monitors' && <Monitors />}
        {activeSection === 'signals' && <SignalHistory />}
        {activeSection === 'settings' && <Settings />}
      </main>
    </div>
  );
}

export default App;